import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useLanguage } from "@/hooks/useLanguage";
import LanguageSelector from "@/components/LanguageSelector";
import api from "@/lib/api";
import { ArrowRight, ExternalLink, Truck, Store, Users, ShoppingCart, Receipt, BarChart3, FileText, CreditCard, Package, ChevronLeft, ChevronRight, X, ChevronDown, ChevronUp, CheckCircle2, MessageCircle } from "lucide-react";

const Landing = () => {
  const { t } = useLanguage();
  const [selectedImage, setSelectedImage] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ name: "", phone: "+" });
  const [contactSubmitting, setContactSubmitting] = useState(false);

  const featureKeys = [
    { key: "routeManagement", icon: Truck, color: "bg-blue-100", iconColor: "text-primary" },
    { key: "shopManagement", icon: Store, color: "bg-purple-100", iconColor: "text-purple-600" },
    { key: "salesmanTracking", icon: Users, color: "bg-green-100", iconColor: "text-green-600" },
    { key: "purchaseManagement", icon: ShoppingCart, color: "bg-orange-100", iconColor: "text-orange-600" },
    { key: "expenseTracking", icon: Receipt, color: "bg-pink-100", iconColor: "text-pink-600" },
    { key: "transactionReports", icon: CreditCard, color: "bg-cyan-100", iconColor: "text-cyan-600" },
    { key: "dailySummary", icon: FileText, color: "bg-indigo-100", iconColor: "text-indigo-600" },
    { key: "profitLoss", icon: BarChart3, color: "bg-amber-100", iconColor: "text-amber-600" },
    { key: "initialLoading", icon: Package, color: "bg-red-100", iconColor: "text-red-600" }
  ];

  const screenshots = [
    {
      id: 1,
      title: "Interactive Dashboard",
      description: "Get a complete real-time overview of revenue metrics, active routes, and key business alerts.",
      image: "https://customer-assets.emergentagent.com/job_codebase-explorer-45/artifacts/gyy1vlut_EggDemo-Interactive%20Dashboard.png",
      alt: "wholesale distribution management dashboard showing revenue metrics, routes, and business alerts"
    },
    {
      id: 2,
      title: "Daily Operations Summary",
      description: "Track daily sales, dispatch numbers, expenses, and gross margins in one view.",
      image: "https://customer-assets.emergentagent.com/job_codebase-explorer-45/artifacts/72rm3fs1_EggDemo-Daily-Summary.png",
      alt: "wholesale distribution daily operations summary with dispatch, expenses, and margin tracking"
    },
    {
      id: 3,
      title: "Route Management",
      description: "Organize delivery routes, assign drivers, and monitor territory dues.",
      image: "https://customer-assets.emergentagent.com/job_codebase-explorer-45/artifacts/xfcouwyt_EggDemo-Route-Management.png",
      alt: "B2B distribution route management screen for organizing territories and tracking dues"
    },
    {
      id: 4,
      title: "Client Account Management",
      description: "Maintain credit thresholds, customer ledgers, and returnable container balances.",
      image: "https://customer-assets.emergentagent.com/job_codebase-explorer-45/artifacts/hcin95n6_EggDemo-Shop-Management.png",
      alt: "wholesale client account management for tracking buyer credit, ledgers, and returnable containers"
    },
    {
      id: 5,
      title: "Transaction Reports",
      description: "Review historical sales and payment collections with advanced category filters.",
      image: "https://customer-assets.emergentagent.com/job_codebase-explorer-45/artifacts/xoafppg0_EggDemo-Transaction-Report.png",
      alt: "wholesale transaction report with sales, payment collection, and category filters"
    },
    {
      id: 6,
      title: "Field Sales Management",
      description: "Manage rep routes, track field collections, and balance day-end returns.",
      image: "https://customer-assets.emergentagent.com/job_codebase-explorer-45/artifacts/m1yg4wq6_EggDemo-Salesman-Management.png",
      alt: "field sales management software for rep routes, collections, and day-end returns"
    },
    {
      id: 7,
      title: "Purchasing & Vendor Management",
      description: "Track purchase orders, incoming stock, and pending vendor payments.",
      image: "https://customer-assets.emergentagent.com/job_codebase-explorer-45/artifacts/eu2fquoc_EggDemo-Purchase-Management.png",
      alt: "wholesale purchasing and vendor management for incoming stock and supplier payments"
    },
    {
      id: 8,
      title: "Expense Tracking",
      description: "Monitor operational costs categorized by fleet, labor, and warehouse expenses.",
      image: "https://customer-assets.emergentagent.com/job_codebase-explorer-45/artifacts/f6ptmpsi_EggDemo-Expense-Management.png",
      alt: "distribution business expense tracking for fleet, labor, and warehouse costs"
    },
    {
      id: 9,
      title: "Profit & Loss Analytics",
      description: "Review calculated net profit backed by complete revenue and expense data.",
      image: "https://customer-assets.emergentagent.com/job_codebase-explorer-45/artifacts/4hc0gjug_EggDemo-Profit-Loss.png",
      alt: "wholesale distribution profit and loss analytics showing revenue, costs, and net profit"
    }
  ];

  const targetAudience = [
    "Building Materials & Hardware — plywood, tiles, sanitaryware, paint, lumber, and steel",
    "FMCG & Packaged Foods — beverages, grain, snacks, personal care, and packaged goods",
    "Consumer Electronics & Appliances — components, accessories, and home appliances",
    "Textiles & Apparel — fabric rolls, ready-made garments, and home furnishings",
    "Industrial Goods & Spare Parts — automotive spares, tools, and packaging materials",
    "Poultry & Agricultural Logistics — perishables, wholesale produce, dairy, and feed"
  ];

  const faqs = [
    {
      question: "What is LitSpark Wholesale Distribution Software?",
      answer: "It is a comprehensive cloud platform designed to manage sales routes, inventory dispatch, field collections, vendor purchasing, and financial reporting for B2B supply businesses."
    },
    {
      question: "Can this software handle multi-category inventory?",
      answer: "Yes. Whether you sell by volume, weight, unit count, pack, or crate, the platform can be configured to manage your inventory metrics, packaging units, batch codes, and returnable containers."
    },
    {
      question: "Does it track outstanding customer payments and credit limits?",
      answer: "Yes. You can set customized credit limits for each buyer, track pending payment terms, and lock accounts that exceed payment timelines."
    },
    {
      question: "Can field sales agents submit daily payment collections via mobile?",
      answer: "Yes. Field reps can log cash, cheque, UPI, or bank-transfer collections on their routes, allowing instant daily balance reconciliations."
    }
  ];

  const handlePrevImage = () => {
    setSelectedImage(prev => prev === 0 ? screenshots.length - 1 : prev - 1);
  };

  const handleNextImage = () => {
    setSelectedImage(prev => prev === screenshots.length - 1 ? 0 : prev + 1);
  };

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    setContactSubmitting(true);
    try {
      await api.post("/public/distribution-contact", contactForm);
      toast.success("Enquiry sent successfully", {
        description: "Thank you! The LitSpark team will contact you shortly.",
      });
      setContactOpen(false);
      setContactForm({ name: "", phone: "+" });
    } catch (error) {
      toast.error("Could not send enquiry", {
        description: error.response?.data?.detail || "Please try again shortly.",
      });
    } finally {
      setContactSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white" data-testid="landing-page">
      {/* Header */}
      <header className="container mx-auto px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-white rounded-xl p-2 shadow-md">
            <img
              src="/logo.png"
              alt="LitSpark Wholesaler distribution management software logo"
              className="w-12 h-12"
            />
          </div>
          <div className="hidden sm:block">
            <span className="text-xl font-semibold text-primary-950">LitSpark Wholesaler</span>
            <p className="text-sm text-muted-foreground">The All-in-One Wholesale &amp; B2B Distribution Management Software</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <LanguageSelector />
          <Button
            onClick={() => setContactOpen(true)}
            data-testid="admin-login-btn"
            className="rounded-full bg-primary hover:bg-primary-600 text-white"
          >
            Try Demo
            <ArrowRight size={18} className="ml-2" />
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <main className="container mx-auto px-6 py-16">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          <div className="flex-1 text-center lg:text-left">
            <h1 className="text-4xl lg:text-5xl font-semibold text-primary-950 leading-tight mb-6">
              Streamline Your Wholesale Operations from One Central Dashboard
            </h1>
            <div className="text-lg text-muted-foreground mb-8 max-w-xl space-y-4">
              <p>
                LitSpark Wholesale Distribution Software empowers B2B distributors, stockists, and suppliers to digitize their entire operational supply chain.
              </p>
              <p>
                Effortlessly manage delivery routes, track retail client accounts, monitor sales rep collections, log vendor purchases, control expenses, and generate automated real-time Profit &amp; Loss reports—no matter what product you distribute.
              </p>
              <p>
                Built for modern B2B wholesalers, stockists, and distributors looking to scale efficiently and protect margins.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button
                onClick={() => setContactOpen(true)}
                size="lg"
                data-testid="get-started-btn"
                className="rounded-full bg-primary hover:bg-primary-600 text-white text-lg px-8"
              >
                Try the Demo Now
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </div>
          </div>
          <div className="flex-1 flex flex-col items-center gap-4">
            <div className="bg-white rounded-2xl p-6 shadow-xl">
              <img
                src="/logo.png"
                alt="LitSpark Wholesaler complete B2B distribution management system"
                className="w-64 h-64 lg:w-72 lg:h-72"
              />
            </div>
            <a
              href="https://litspark.solutions/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-primary hover:text-primary-600 font-medium transition-colors"
            >
              Know More about LitSpark
              <ExternalLink size={16} />
            </a>
          </div>
        </div>

        {/* Features Grid */}
        <section className="mt-20" aria-labelledby="features-heading">
          <p className="text-primary text-center font-semibold mb-2">About LitSpark Wholesaler</p>
          <h2 id="features-heading" className="text-2xl lg:text-3xl font-semibold text-primary-950 text-center mb-12">
            {t.landing.featuresTitle}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featureKeys.map((feature) => {
              const Icon = feature.icon;
              const featureData = t.landing.features[feature.key];
              return (
                <article key={feature.key} className="bg-white rounded-2xl p-6 shadow-sm border border-border/50">
                  <div className={`w-12 h-12 rounded-xl ${feature.color} flex items-center justify-center mb-4`}>
                    <Icon size={24} className={feature.iconColor} />
                  </div>
                  <h3 className="text-lg font-semibold text-primary-950 mb-2">{featureData.title}</h3>
                  <p className="text-muted-foreground text-sm">
                    {featureData.desc}
                  </p>
                </article>
              );
            })}
          </div>
        </section>

        {/* Who Is This Software For Section */}
        <section className="mt-20" aria-labelledby="audience-heading">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-3xl p-8 lg:p-12">
            <p className="text-primary text-center font-semibold mb-2">Versatile Use Cases</p>
            <h2 id="audience-heading" className="text-2xl lg:text-3xl font-semibold text-primary-950 text-center mb-4">
            Built for Any Wholesale &amp; B2B Distribution Business
            </h2>
            <p className="text-muted-foreground text-center mb-8 max-w-2xl mx-auto">
              Our enterprise software adapts to your product catalog and supply-chain workflows, including dimensions, volume, weight, batch codes, serialized inventory, and returnable packaging.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {targetAudience.map((audience, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-2 bg-white px-5 py-3 rounded-full shadow-sm border border-border/50"
                >
                  <CheckCircle2 size={18} className="text-primary" />
                  <span className="font-medium text-primary-950">{audience}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Screenshots Gallery */}
        <section className="mt-20" aria-labelledby="screenshots-heading">
          <p className="text-primary text-center font-semibold mb-2">Interactive Dashboard Demo</p>
          <h2 id="screenshots-heading" className="text-2xl lg:text-3xl font-semibold text-primary-950 text-center mb-4">
            See It In Action
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            Explore our flexible distribution dashboard with these screenshots. Click any screenshot to preview.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {screenshots.map((screenshot, index) => (
              <article 
                key={screenshot.id}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-border/50 hover:shadow-lg transition-all duration-300 cursor-pointer"
                onClick={() => setSelectedImage(index)}
              >
                <div className="relative aspect-video overflow-hidden bg-gray-100">
                  <img
                    src={screenshot.image}
                    alt={screenshot.alt}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                    <span className="text-white text-sm font-medium">Click to enlarge</span>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-base font-semibold text-primary-950 mb-1">{screenshot.title}</h3>
                  <p className="text-muted-foreground text-sm line-clamp-2">{screenshot.description}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mt-20" aria-labelledby="faq-heading">
          <h2 id="faq-heading" className="text-2xl lg:text-3xl font-semibold text-primary-950 text-center mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            Common questions about our wholesale distribution management platform
          </p>
          
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl border border-border/50 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                  aria-expanded={openFaq === index}
                >
                  <h3 className="font-semibold text-primary-950 pr-4">{faq.question}</h3>
                  {openFaq === index ? (
                    <ChevronUp size={20} className="text-primary flex-shrink-0" />
                  ) : (
                    <ChevronDown size={20} className="text-muted-foreground flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5">
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* About LitSpark Section */}
        <section className="mt-20" aria-labelledby="about-heading">
          <div className="bg-gradient-to-br from-primary-950 to-blue-900 rounded-3xl p-8 lg:p-12 text-center">
            <h2 id="about-heading" className="text-2xl lg:text-3xl font-semibold text-white mb-6">
              About LitSpark
            </h2>
            <p className="text-blue-100 text-lg leading-relaxed max-w-4xl mx-auto mb-8">
              LitSpark Solutions is an agile outsourcing, development, and marketing agency headquartered in Bengaluru, serving global enterprise clients. We offer end-to-end technical product engineering, resource outsourcing, and growth marketing strategies designed to scale B2B platforms globally.
            </p>
            <a
              href="https://litspark.solutions/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-primary-950 font-semibold rounded-full hover:bg-blue-50 transition-colors"
            >
              Visit LitSpark Website
              <ExternalLink size={18} />
            </a>
          </div>
        </section>

        {/* CTA Section */}
        <section className="mt-20" aria-labelledby="cta-heading">
          <div className="bg-white rounded-3xl p-8 lg:p-12 text-center border-2 border-primary/20 shadow-lg">
            <h2 id="cta-heading" className="text-2xl lg:text-3xl font-semibold text-primary-950 mb-4">
              Ready to Scale Your Wholesale Business?
            </h2>
            <p className="text-muted-foreground text-lg mb-4 max-w-2xl mx-auto">
              Transform your distribution operations with LitSpark Wholesale Software. Take full control of your routes, collections, vendor bills, and financial health today.
            </p>
            <Button
              onClick={() => setContactOpen(true)}
              size="lg"
              data-testid="cta-demo-btn"
              className="rounded-full bg-primary hover:bg-primary-600 text-white text-lg px-10 py-6"
            >
              Start Exploring the Demo Now
              <ArrowRight size={22} className="ml-2" />
            </Button>
          </div>
        </section>
      </main>

      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent className="sm:max-w-md">
          <form onSubmit={handleContactSubmit}>
            <DialogHeader>
              <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-700 sm:mx-0">
                <MessageCircle size={24} />
              </div>
              <DialogTitle>Explore the LitSpark Wholesaler Demo</DialogTitle>
              <DialogDescription>
                Share your details and our team will contact you with demo access.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-6">
              <div className="space-y-2">
                <Label htmlFor="contact-name">Name</Label>
                <Input
                  id="contact-name"
                  name="name"
                  value={contactForm.name}
                  onChange={(event) => setContactForm((current) => ({ ...current, name: event.target.value }))}
                  placeholder="Enter your full name"
                  minLength={2}
                  maxLength={100}
                  autoComplete="name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-phone">WhatsApp number</Label>
                <Input
                  id="contact-phone"
                  name="phone"
                  type="tel"
                  value={contactForm.phone}
                  onChange={(event) => {
                    const enteredPhone = event.target.value;
                    const phoneWithPrefix = enteredPhone.startsWith("+")
                      ? `+${enteredPhone.slice(1).replace(/\+/g, "")}`
                      : `+${enteredPhone.replace(/\+/g, "")}`;
                    setContactForm((current) => ({ ...current, phone: phoneWithPrefix }));
                  }}
                  placeholder="e.g. +91 98765 43210"
                  autoComplete="tel"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Please include your country code, such as +91 for India.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button type="submit" disabled={contactSubmitting} className="w-full bg-green-600 text-white hover:bg-green-700 sm:w-auto">
                {contactSubmitting ? "Sending…" : "Submit Details"}
                {!contactSubmitting && <MessageCircle size={18} className="ml-2" />}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Lightbox Modal */}
      {selectedImage !== null && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Image lightbox"
        >
          {/* Close button */}
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            aria-label="Close lightbox"
          >
            <X size={24} />
          </button>
          
          {/* Previous button */}
          <button
            onClick={(e) => { e.stopPropagation(); handlePrevImage(); }}
            className="absolute left-4 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            aria-label="Previous image"
          >
            <ChevronLeft size={28} />
          </button>
          
          {/* Image container */}
          <div 
            className="max-w-6xl max-h-[85vh] w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={screenshots[selectedImage].image}
              alt={screenshots[selectedImage].alt}
              className="w-full h-auto max-h-[75vh] object-contain rounded-lg"
            />
            <div className="mt-4 text-center">
              <h3 className="text-xl font-semibold text-white mb-2">{screenshots[selectedImage].title}</h3>
              <p className="text-white/70 text-sm max-w-xl mx-auto">{screenshots[selectedImage].description}</p>
              <div className="mt-3 flex justify-center gap-2">
                {screenshots.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      index === selectedImage ? "bg-white" : "bg-white/30 hover:bg-white/50"
                    }`}
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
          
          {/* Next button */}
          <button
            onClick={(e) => { e.stopPropagation(); handleNextImage(); }}
            className="absolute right-4 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            aria-label="Next image"
          >
            <ChevronRight size={28} />
          </button>
        </div>
      )}

      {/* Footer */}
      <footer className="container mx-auto px-6 py-8 border-t border-border/50 mt-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-muted-foreground text-base">
            © 2026{" "}
            <a 
              href="https://litspark.solutions/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              LitSpark Solutions Private Limited
            </a>
            . All rights reserved.
          </p>
          <a
            href="https://litspark.solutions/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-primary hover:text-primary-600 font-medium transition-colors"
          >
            Visit LitSpark Solutions
            <ExternalLink size={16} />
          </a>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
